import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import InviteAccept from "@/pages/InviteAccept";
import JoinSignup from "@/pages/JoinSignup";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import Organisations from "@/pages/admin/Organisations";
import OrgDetail from "@/pages/admin/OrgDetail";
import SuperAdmins from "@/pages/admin/SuperAdmins";
import AuditLog from "@/pages/admin/AuditLog";
import EmployeeDetail from "@/pages/admin/EmployeeDetail";
import ChangeRequests from "@/pages/admin/ChangeRequests";
import ExportTemplates from "@/pages/admin/ExportTemplates";
import PayrollReview from "@/pages/admin/PayrollReview";
import FinalRosters from "@/pages/admin/FinalRosters";
import FinalRosterView from "@/pages/FinalRosterView";

import Dashboard from "@/pages/org/Dashboard";
import Employees from "@/pages/org/Employees";
import InviteEmployees from "@/pages/org/InviteEmployees";
import PendingSignups from "@/pages/org/PendingSignups";
import JoinLinkPage from "@/pages/org/JoinLink";
import Masters from "@/pages/org/Masters";
import Holidays from "@/pages/org/Holidays";
import OrgAdmins from "@/pages/org/OrgAdmins";
import Payroll from "@/pages/org/Payroll";
import Roster from "@/pages/org/Roster";
import CenterManagers from "@/pages/org/CenterManagers";

import MyForm from "@/pages/employee/MyForm";
import JoiningForm from "@/pages/employee/JoiningForm";
import { NotFound, Forbidden } from "@/pages/NotFound";

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function EmployeeOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  const isEmployee = user.memberships?.some((m) => m.role === "employee");
  if (!isEmployee && ["super_admin", "org_admin", "center_manager"].some((r) => user.roles?.includes(r))) return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.roles?.includes("super_admin")) return <Navigate to="/admin/organisations" replace />;
  if (user.roles?.includes("org_admin")) return <Navigate to="/org/dashboard" replace />;
  if (user.roles?.includes("center_manager")) return <Navigate to="/org/roster" replace />;
  return <Navigate to="/employee" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/join/:token" element={<JoinSignup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/admin/organisations" element={<Organisations />} />
        <Route path="/admin/organisations/:id" element={<OrgDetail />} />
        <Route path="/admin/super-admins" element={<SuperAdmins />} />
        <Route path="/admin/audit-log" element={<AuditLog />} />
        <Route path="/admin/change-requests" element={<ChangeRequests crossOrg />} />
        <Route path="/admin/export-templates" element={<ExportTemplates />} />
        <Route path="/admin/payroll-review" element={<PayrollReview />} />
        <Route path="/admin/final-rosters" element={<FinalRosters />} />
        <Route path="/final-rosters/:fid" element={<FinalRosterView />} />
        <Route path="/org/dashboard" element={<Dashboard />} />
        <Route path="/org/employees" element={<Employees />} />
        <Route path="/org/employees/:id" element={<EmployeeDetail />} />
        <Route path="/org/invite" element={<InviteEmployees />} />
        <Route path="/org/pending-signups" element={<PendingSignups />} />
        <Route path="/org/join-link" element={<JoinLinkPage />} />
        <Route path="/org/masters" element={<Masters />} />
        <Route path="/org/holidays" element={<Holidays />} />
        <Route path="/org/org-admins" element={<OrgAdmins />} />
        <Route path="/org/change-requests" element={<ChangeRequests />} />
        <Route path="/org/export-templates" element={<ExportTemplates />} />
        <Route path="/org/payroll" element={<Payroll />} />
        <Route path="/org/roster" element={<Roster />} />
        <Route path="/org/center-managers" element={<CenterManagers />} />
      </Route>

      <Route path="/employee" element={<EmployeeOnly><JoiningForm /></EmployeeOnly>} />

      <Route path="/403" element={<Forbidden />} />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors mobileOffset={{ top: 96 }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
